import { useMemo, useState } from 'react'
import { deckSuggestions, type DeckSuggestion } from '@aurora/core'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { interests } from '../../store/selectors'
import { pushToast } from '../../lib/toast'
import { Sheet } from '../ui/Sheet'
import type { useCards } from './useCards'

type Cards = ReturnType<typeof useCards>

/**
 * A new deck from one sentence. The suggestions climb the method (capitals,
 * then lowercase the next day, numbers, sight words) and then turn personal:
 * a family language, the things she loves. Tapping one fills the sentence
 * rather than sending it, so the parent can make it hers first.
 */
export function NewDeckSheet({ cards, onClose }: { cards: Cards; onClose: () => void }) {
  const loves = useAuroraStore(interests) as any[]
  const [direction, setDirection] = useState('')
  const [picked, setPicked] = useState<DeckSuggestion | null>(null)
  const name = cards.child?.name ?? 'her'

  const suggestions = useMemo(
    () =>
      deckSuggestions({
        decks: cards.decks.flatMap((d) => (d.deck ? [d.deck] : [{ id: '', title: d.artifact.title ?? '', direction: d.artifact.generation?.prompt ?? '', cards: [] }])),
        interests: loves.map((m) => m.statement as string),
        about: [cards.child?.about ?? ''],
        memories: cards.memory,
        now: cards.now,
      }),
    [cards.decks, cards.child, cards.memory, cards.now, loves],
  )

  const make = () => {
    const text = direction.trim()
    if (!text || !cards.child) return
    const s = auroraStore.getState() as any
    if (typeof s.createDeck !== 'function') {
      pushToast('Making decks is not ready yet', { tone: 'error' })
      return
    }
    s.createDeck({
      childId: cards.child._id,
      direction: text,
      ...(picked?.starter && picked.direction === text ? { starter: picked.starter } : {}),
    })
    pushToast(`Writing the deck. It joins ${name}'s cards when it is ready.`)
    onClose()
  }

  return (
    <Sheet
      open
      onClose={onClose}
      eyebrow="New deck"
      title="What should it teach?"
      footer={
        <button className="btn btn-primary btn-lg new-deck-go" onClick={make} disabled={!direction.trim()}>
          Make the deck
        </button>
      }
    >
      <form
        className="new-deck"
        onSubmit={(e) => {
          e.preventDefault()
          make()
        }}
      >
        <label className="cards-field">
          <span className="cards-field-label">In a sentence</span>
          <textarea
            className="field-input new-deck-input"
            rows={2}
            autoFocus
            value={direction}
            maxLength={240}
            placeholder="Hebrew letters, the planets, trees by their leaves…"
            onChange={(e) => {
              setDirection(e.target.value)
              setPicked(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                make()
              }
            }}
          />
          <span className="field-hint">
            Sapling writes a ladder of cards around {name}: easy first, a few new ones a day, in
            the voice she already knows.
          </span>
        </label>

        {suggestions.length > 0 && (
          <div className="new-deck-ideas">
            <p className="new-deck-ideas-label">Ideas for {name}</p>
            <div className="chips">
              {suggestions.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  className={`chip${picked === s ? ' is-active' : ''}`}
                  onClick={() => {
                    setDirection(s.direction)
                    setPicked(s)
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>
    </Sheet>
  )
}
